import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Typography,
  Button,
  Chip,
  Stack,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import CancelOutlined from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CancelKOTDialog from "../../pages/kot-view/CancelKOTDialog";
import { useNotify } from "../../utils/snackbarUtils";
import { getOrderKots } from "../../reduxStore/slices/cartSlice";
import { useDispatch } from "react-redux";

// Architectural Status Constants
const KOT_STATUS = {
  PENDING: { label: "Cooking", color: "#ed6c02", bg: "#fff7ed" }, // Orange
  READY: { label: "Ready", color: "#2e7d32", bg: "#f0fdf4" }, // Green
  SERVED: { label: "Served", color: "#757575", bg: "#f9fafb" }, // Gray
};

function OrderKOTViewDrawer({
  isOpen,
  closeOrderKOTDrawer,
  activeOrderId,
  storeId,
  tableInfo = "A1", // Default to A1 as seen in your table view
}) {
  const { showSuccess, handleApiError } = useNotify();
  const dispatch = useDispatch();

  const [kotOrders, setKotOrders] = useState([]);
  const [isShowCancelKOTDialog, setIsShowCancelKOTDialog] = useState(false);
  const [selectedKOT, setSelectedKOT] = useState(null);

  // Fetch KOTs
  const fetchOrderKots = async () => {
    if (!storeId || !activeOrderId) return;
    try {
      const response = await dispatch(
        getOrderKots({ storeId, activeOrderId })
      ).unwrap();
      setKotOrders(response?.kots || []);
    } catch (err) {
      handleApiError(err);
    }
  };

  useEffect(() => {
    if (isOpen) fetchOrderKots();
  }, [isOpen, storeId, activeOrderId]);

  // Actions
  const handleReprint = (kot) => {
    showSuccess(`Reprinting KOT #${kot.kotNumber}...`);
    // TODO: Connect to your Electron/Silent print service
  };

  const handleMarkServed = (kotId) => {
    showSuccess(`KOT #${kotId} marked as Served`);
    // TODO: API call update status
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={() => closeOrderKOTDrawer(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: 420 },
            bgcolor: "#f4f6f8",
          },
        }}
      >
        {/* --- STICKY HEADER --- */}
        {/* <Box
          p={2}
          bgcolor="white"
          borderBottom="1px solid #e0e0e0"
          position="sticky"
          top={0}
          zIndex={10}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight={700}>
              Kots (Order #{activeOrderId})
            </Typography>
            <IconButton onClick={() => closeOrderKOTDrawer(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Stack direction="row" spacing={1} mt={1} alignItems="center">
            <Divider orientation="vertical" flexItem />
            <Typography variant="body2" color="text.secondary">
              Order #{activeOrderId}
            </Typography>

            <Typography variant="body1" fontWeight={600}>
              Order: #{activeOrderId}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Customer: Rajendra A.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Table: D3 (Rooftop) / Dine In
            </Typography>
            <Box mt={1} display="flex" gap={1} flexWrap="wrap">
              <Chip label={`Total KOTs: ${kotOrders.length}`} size="small" />
              <Chip
                label={`Total Items: ${kotOrders.reduce(
                  (acc, k) => acc + k.items.length,
                  0
                )}`}
                size="small"
                color="primary"
              />
            </Box>
          </Stack>
        </Box> */}

        <Box
          p={2}
          bgcolor="white"
          borderBottom="1px solid #e0e0e0"
          position="sticky"
          top={0}
          zIndex={10}
        >
          {/* Header title + close */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <Typography variant="h6" fontWeight={700}>
                KOT Details
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                (Order: #{activeOrderId})
              </Typography>
            </Box>
            <IconButton onClick={() => closeOrderKOTDrawer(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Order Info */}
          <Stack
            direction="row"
            spacing={1}
            mt={1}
            flexWrap="wrap"
            alignItems="center"
            divider={<Divider orientation="vertical" flexItem />}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Table: D3 (Rooftop) / Dine In
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Customer: Rajendra A.
            </Typography>
          </Stack>
        </Box>

        {/* --- KOT LIST --- */}
        <Box p={2} display="flex" flexDirection="column" gap={2}>
          {/* Chips */}
          <Box mt={1} display="flex" gap={1} flexWrap="wrap">
            <Chip label={`Total KOTs: ${kotOrders.length}`} size="small" />
            <Chip
              label={`Total Items: ${kotOrders.reduce(
                (acc, k) => acc + k.items.length,
                0
              )}`}
              size="small"
              color="primary"
            />
          </Box>

          {kotOrders.length === 0 ? (
            <Box textAlign="center" py={10}>
              <Typography color="text.secondary">
                No kitchen tickets found.
              </Typography>
            </Box>
          ) : (
            kotOrders.map((kot) => {
              const status = KOT_STATUS[kot.status] || KOT_STATUS.PENDING;

              return (
                <Box
                  key={kot.order_kot_uid}
                  sx={{
                    bgcolor: "white",
                    borderRadius: 2,
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    borderLeft: `6px solid ${status.color}`, // Visual indicator
                    overflow: "hidden",
                  }}
                >
                  {/* KOT Card Header */}
                  <Box
                    p={1.5}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    bgcolor={status.bg}
                  >
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>
                        KOT #{kot.kot_number}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <AccessTimeIcon
                          sx={{ fontSize: 14, color: "text.secondary" }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {kot.createdAt} {/* Relative time */}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={status.label}
                      size="small"
                      sx={{
                        bgcolor: status.color,
                        color: "white",
                        fontWeight: 600,
                      }}
                    />
                  </Box>

                  {/* KOT Items & Add-ons */}
                  <Box p={2}>
                    {kot.items?.map((item, idx) => (
                      <Box key={idx} mb={1.5}>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" fontWeight={600}>
                            {item.variant_name}
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {item.quantity}
                          </Typography>
                        </Box>
                        {/* Render Add-ons if present */}
                        {item.addons?.map((addon, aIdx) => (
                          <Typography
                            key={aIdx}
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ pl: 1, fontStyle: "italic" }}
                          >
                            + {addon.name} (₹{addon.price})
                          </Typography>
                        ))}
                      </Box>
                    ))}

                    {/* Cooking Instructions */}
                    {kot.instructions && (
                      <Box
                        mt={1}
                        p={1}
                        bgcolor="#fffbea"
                        border="1px dashed #ffe58f"
                        borderRadius={1}
                      >
                        <Typography variant="caption" color="#856404">
                          <b>Note:</b> {kot.instructions}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Card Actions */}
                  <Divider sx={{ borderStyle: "dashed" }} />
                  <Box
                    p={1}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Button
                      startIcon={<CancelOutlined />}
                      color="error"
                      size="small"
                      onClick={() => {
                        setSelectedKOT(kot);
                        setIsShowCancelKOTDialog(true);
                      }}
                    >
                      Cancel
                    </Button>
                    <Box>
                      <Tooltip title="Reprint KOT">
                        <IconButton
                          size="small"
                          onClick={() => handleReprint(kot)}
                        >
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Button
                        variant="contained"
                        size="small"
                        color="success"
                        startIcon={<CheckCircleOutlineIcon />}
                        sx={{ ml: 1, textTransform: "none" }}
                        onClick={() => handleMarkServed(kot.kotNumber)}
                      >
                        Mark Served
                      </Button>
                    </Box>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Drawer>

      <CancelKOTDialog
        isCancelDialog={isShowCancelKOTDialog}
        onKeepKOTClick={() => setIsShowCancelKOTDialog(false)}
        onCancelKOTClick={() => {
          setIsShowCancelKOTDialog(false);
          showSuccess(`KOT #${selectedKOT?.kotNumber} voided successfully`);
          setKotOrders((prev) => prev.filter((k) => k.id !== selectedKOT.id));
        }}
      />
    </>
  );
}

export default OrderKOTViewDrawer;
